import { cloneElement, useEffect, useRef } from 'react';
import { useTranslationContext, useTranslations } from '../../i18n/TranslationProvider';

import logoSBKWithClaimGerman from '../../logo/footer/logo-sbk-with-claim.svg';
import logoSBKWithClaimEnglish from '../../logo/footer/logo-sbk-with-claim-en.svg';

import logoFacebook from '../../logo/socialmedia/icon-social-media-facebook.svg';
import logoInstagram from '../../logo/socialmedia/icon-social-media-instagram.svg';
import logoYouTube from '../../logo/socialmedia/icon-social-media-youtube.svg';
import logoX from '../../logo/socialmedia/icon-social-media-x.svg';
import logoLinkedIn from '../../logo/socialmedia/icon-social-media-linked-in.svg';
import logoXing from '../../logo/socialmedia/icon-social-media-xing.svg';
import logoTikTok from '../../logo/socialmedia/icon-social-media-tiktok.svg';

const footerLinks = [
    [
        'footerPrivacy',
        'https://www.sbk.org/service-navigation/datenschutz/',
        'https://www.sbk.org/sbk-en/service-navigation/privacy/',
    ],
    [
        'footerLegalInformation',
        'https://www.sbk.org/service-navigation/impressum/',
        'https://www.sbk.org/sbk-en/service-navigation/legal-information/',
    ],
    [
        'footerAccessibility',
        'https://www.sbk.org/service-navigation/barrierefreiheit-leichte-sprache/',
        'https://www.sbk.org/sbk-en/service-navigation/accessibility-simplified-language/',
    ],
];

/**
 * Wrapper for the sbk-service-navigation-item as it does not provide an option to pass the target and rel attributes
 * to the underlying anchor element.
 */
const OpenInNewTab = ({ children }) => {
    const ref = useRef();

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const link = ref.current.shadowRoot.querySelector('sbk-link')?.shadowRoot.querySelector('a');
        if (link) {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        }
    }, [ref.current]);

    return cloneElement(children, {
        ref,
    });
};

const Footer = () => {
    const { locale } = useTranslationContext();
    const { sbkLogoAltText, ...translations } = useTranslations();
    const logoPath = locale === 'en' ? logoSBKWithClaimEnglish : logoSBKWithClaimGerman;

    return (
        <sbk-footer
            logo-path={logoPath}
            logo-alt-text={sbkLogoAltText}
            copyright-text={`${(new Date()).getFullYear()} Siemens-Betriebskrankenkasse`}
        >
            <sbk-service-navigation slot="serviceNavigation">
                {footerLinks.map(([labelKey, hrefDe, hrefEn]) => (
                    <OpenInNewTab key={labelKey}>
                        <sbk-service-navigation-item
                            href={locale !== 'en' ? hrefDe : hrefEn}
                            variant="regular"
                            size="xs"
                        >
                            {translations[labelKey]}
                        </sbk-service-navigation-item>
                    </OpenInNewTab>
                ))}
            </sbk-service-navigation>
            <div slot="socialMediaIcons">
                <sbk-round-button href="https://de-de.facebook.com/SBK/" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="Facebook"
                            loading="lazy"
                            src={logoFacebook}
                            width="24"
                            height="24"
                            data-focus-area="{&quot;x&quot;:8,&quot;y&quot;:8,&quot;width&quot;:8,&quot;height&quot;:8}"
                        />
                    </div>
                </sbk-round-button>
                <sbk-round-button href="https://www.instagram.com/sbk_de/" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="Instagram"
                            loading="lazy"
                            src={logoInstagram}
                            width="24"
                            height="24"
                        />
                    </div>
                </sbk-round-button>
                <sbk-round-button href="https://www.youtube.com/user/SiemensBKK" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="YouTube"
                            loading="lazy"
                            src={logoYouTube}
                            width="24"
                            height="24"
                            data-focus-area="{&quot;x&quot;:8,&quot;y&quot;:8,&quot;width&quot;:8,&quot;height&quot;:8}"
                        />
                    </div>
                </sbk-round-button>
                <sbk-round-button href="https://x.com/siemens_bkk?lang=de" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="X"
                            loading="lazy"
                            src={logoX}
                            width="24"
                            height="24"
                            data-focus-area="{&quot;x&quot;:8,&quot;y&quot;:8,&quot;width&quot;:8,&quot;height&quot;:8}"
                        />
                    </div>
                </sbk-round-button>
                <sbk-round-button href="https://de.linkedin.com/company/sbk-siemens-betriebskrankenkasse" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="LinkedIn"
                            loading="lazy"
                            src={logoLinkedIn}
                            width="24"
                            height="24"
                            data-focus-area="{&quot;x&quot;:8,&quot;y&quot;:8,&quot;width&quot;:8,&quot;height&quot;:8}"
                        />
                    </div>
                </sbk-round-button>
                <sbk-round-button href="https://www.xing.com/pages/sbk-siemens-betriebskrankenkasse" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="XING"
                            loading="lazy"
                            src={logoXing}
                            width="24"
                            height="24"
                            data-focus-area="{&quot;x&quot;:8,&quot;y&quot;:8,&quot;width&quot;:8,&quot;height&quot;:8}"
                        />
                    </div>
                </sbk-round-button>
                <sbk-round-button href="https://www.tiktok.com/@synapsengefluester" title="">
                    <div slot="icon">
                        <img
                            data-title=""
                            data-publisher=""
                            data-source=""
                            className="image-embed-item  svg"
                            alt="TikTok"
                            loading="lazy"
                            src={logoTikTok}
                            width="24"
                            height="24"
                            data-focus-area="{&quot;x&quot;:8,&quot;y&quot;:8,&quot;width&quot;:8,&quot;height&quot;:8}"
                        />
                    </div>
                </sbk-round-button>
            </div>
        </sbk-footer>
    );
};

export default Footer;
